import { Num } from "@/components/Num";
import { useEffect, useState } from "react";
import { GitBranch, Save, RotateCcw, Trash2, GitCompare, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errorText } from "@/lib/api";
import { fmtPct } from "@/lib/format";
import { toast } from "sonner";

const KPI_KEYS = [["revenue", "Total Revenue"], ["payroll", "Payroll"], ["gop", "GOP"], ["ebitda", "EBITDA"]];

const CompareView = ({ data }) => {
  const [onlyChanged, setOnlyChanged] = useState(true);
  const lines = data.lines.filter((l) => !onlyChanged || (l.diff !== null && Math.abs(l.diff) > 0.5));
  const kpiIds = Object.fromEntries(["revenue", "gop", "ebitda", "nop", "payroll"].flatMap((k) => [data.a.kpis?.[k]?.id, data.b.kpis?.[k]?.id].filter(Boolean).map((id) => [id, k.toUpperCase()])));
  return (
    <div className="space-y-5" data-testid="compare-view">
      <div className="grid sm:grid-cols-4 gap-3">
        {KPI_KEYS.map(([k, label]) => {
          const a = data.a.kpis?.[k]; const b = data.b.kpis?.[k];
          if (!a && !b) return null;
          const diff = a && b ? b.budget_total - a.budget_total : null;
          return (
            <div key={k} className="rounded-lg border border-slate-200 p-3" data-testid={`compare-kpi-${k}`}>
              <p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p>
              <p className="font-mono text-sm text-slate-700 mt-1">A: <Num v={a?.budget_total} compact /></p>
              <p className="font-mono text-sm text-slate-900 font-semibold">B: <Num v={b?.budget_total} compact /></p>
              {diff !== null && <p className={`text-xs font-semibold mt-1 ${diff >= 0 ? "text-emerald-600" : "text-red-600"}`}>{diff >= 0 ? "+" : ""}<Num v={diff} compact /> ({fmtPct(a.budget_total ? diff / Math.abs(a.budget_total) * 100 : null)})</p>}
            </div>
          );
        })}
      </div>
      <div className="grid sm:grid-cols-2 gap-3 text-xs">
        {[data.a, data.b].map((v, i) => (
          <div key={i} className="rounded-lg bg-slate-50 p-3">
            <p className="font-semibold text-slate-900">{i === 0 ? "A" : "B"} · {v.name}</p>
            <p className="text-slate-600 mt-1 font-mono">UMR {v.assumptions?.umr_increase_pct}% · Inflation {v.assumptions?.inflation_pct}% · Manning {v.assumptions?.manning_mode === "headcount" ? "headcount" : `${v.assumptions?.manning_change_pct}%`} · ADR {v.assumptions?.adr_growth_pct}% · Occ {v.assumptions?.occupancy_growth_pct}% · Mgmt fee {v.assumptions?.management_fee_pct || 0}% rev / {v.assumptions?.management_fee_gop_pct || 0}% GOP · SC share {v.assumptions?.sc_mgmt_share_pct || 0}%{v.assumptions?.fees_already_in_pnl ? " · fees in P&L" : ""}</p>
          </div>
        ))}
      </div>
      <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={onlyChanged} onChange={(e) => setOnlyChanged(e.target.checked)} data-testid="compare-only-changed" className="accent-emerald-600" /> Show only lines that changed</label>
      <div className="overflow-auto max-h-[50vh] border border-slate-200 rounded-lg">
        <table className="min-w-full text-xs">
          <thead className="bg-slate-900 text-white sticky top-0">
            <tr><th className="text-left px-3 py-2">Sheet</th><th className="text-left px-3 py-2">Line item</th><th className="text-right px-3 py-2">A total</th><th className="text-right px-3 py-2">B total</th><th className="text-right px-3 py-2">Diff</th><th className="text-right px-3 py-2">Diff %</th></tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.id} className="border-b border-slate-100" data-testid={`compare-row-${l.id}`}>
                <td className="px-3 py-1.5 text-slate-500">{l.sheet}</td>
                <td className={`px-3 py-1.5 ${l.row_type !== "line" ? "font-semibold" : ""}`}>{l.label}{kpiIds[l.id] && <span className="ml-2 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 px-1 text-[9px] uppercase tracking-wider" title="This line is the KPI shown in the cards above" data-testid={`compare-kpi-badge-${l.id}`}>KPI {kpiIds[l.id]}</span>}</td>
                <td className="px-3 py-1.5 text-right font-mono"><Num v={l.a_total} line={l} /></td>
                <td className="px-3 py-1.5 text-right font-mono font-semibold"><Num v={l.b_total} line={l} /></td>
                <td className={`px-3 py-1.5 text-right font-mono ${(l.diff || 0) >= 0 ? "text-emerald-700" : "text-red-600"}`}><Num v={l.diff} line={l} /></td>
                <td className="px-3 py-1.5 text-right font-mono">{fmtPct(l.diff_pct)}</td>
              </tr>
            ))}
            {!lines.length && <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">No differences</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const VersionsPanel = ({ projectId, versions, onChange, onRestored }) => {
  const [name, setName] = useState("");
  const [sel, setSel] = useState([]);
  const [cmp, setCmp] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setSel((s) => s.filter((id) => id === "current" || versions.some((v) => v.id === id))); }, [versions]);

  const save = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/versions`, { name: name.trim() });
      setName("");
      toast.success(`Version "${name.trim()}" saved`);
      onChange();
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  const remove = async (v) => {
    if (!window.confirm(`Delete version "${v.name}"?`)) return;
    try { await api.delete(`/projects/${projectId}/versions/${v.id}`); toast.success("Version deleted"); onChange(); } catch (e) { toast.error(errorText(e)); }
  };
  const restore = async (v) => {
    if (!window.confirm(`Restore "${v.name}" as the working budget? Current assumptions and overrides will be replaced.`)) return;
    try { const { data } = await api.post(`/projects/${projectId}/versions/${v.id}/restore`); toast.success(`Restored "${v.name}"`); onRestored(data); } catch (e) { toast.error(errorText(e)); }
  };
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id].slice(-2)));
  const compare = async () => {
    try { const { data } = await api.get(`/projects/${projectId}/versions/compare`, { params: { a: sel[0], b: sel[1] } }); setCmp(data); } catch (e) { toast.error(errorText(e)); }
  };

  const items = [{ id: "current", name: "Current working budget", current: true }, ...versions];
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="versions-panel">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-heading text-base font-semibold text-slate-900 inline-flex items-center gap-2"><GitBranch size={16} className="text-emerald-600" /> Budget Versions</h3>
        <Button size="sm" variant="outline" disabled={sel.length !== 2} onClick={compare} data-testid="compare-versions-button"><GitCompare size={14} className="mr-1" /> Compare {sel.length}/2</Button>
      </div>
      <p className="text-[11px] text-slate-500 mt-1">Snapshot the current budget as a scenario (v1, v2, ...). Tick two items to compare side by side.</p>
      <div className="mt-4 flex gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. v1 Base case, v2 Aggressive ADR" data-testid="version-name-input" onKeyDown={(e) => e.key === "Enter" && save()} />
        <Button onClick={save} disabled={busy || !name.trim()} className="bg-slate-900 hover:bg-emerald-700 shrink-0" data-testid="save-version-button"><Save size={14} className="mr-1" /> Save</Button>
      </div>
      <ul className="mt-4 divide-y divide-slate-100" data-testid="versions-list">
        {items.map((v) => (
          <li key={v.id} className="flex items-center gap-3 py-2.5" data-testid={`version-item-${v.id}`}>
            <input type="checkbox" checked={sel.includes(v.id)} onChange={() => toggle(v.id)} data-testid={`version-select-${v.id}`} className="accent-emerald-600" />
            <div className="flex-1 min-w-0">
              <p className={`text-sm truncate ${v.current ? "text-emerald-700 font-semibold" : "text-slate-900 font-medium"}`}>{v.name}{v.kind === "reforecast" && <span className="ml-2 inline-block rounded-full bg-amber-100 text-amber-800 text-[10px] px-1.5 py-0.5 font-semibold align-middle" data-testid={`version-kind-${v.id}`}>Reforecast</span>}</p>
              {!v.current && <p className="text-[11px] text-slate-500 font-mono">{new Date(v.created_at).toLocaleString("en-GB")} · Rev <Num v={v.kpis?.revenue?.budget_total} compact /> · GOP <Num v={v.kpis?.gop?.budget_total} compact /></p>}
            </div>
            {!v.current && (
              <>
                <button onClick={() => restore(v)} className="text-slate-400 hover:text-emerald-700" title="Restore" data-testid={`version-restore-${v.id}`}><RotateCcw size={14} /></button>
                <button onClick={() => remove(v)} className="text-slate-400 hover:text-red-600" title="Delete" data-testid={`version-delete-${v.id}`}><Trash2 size={14} /></button>
              </>
            )}
          </li>
        ))}
      </ul>
      <Dialog open={!!cmp} onOpenChange={(o) => !o && setCmp(null)}>
        <DialogContent className="max-w-5xl" data-testid="compare-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Scenario comparison</DialogTitle>
            <DialogDescription>{cmp ? `A: ${cmp.a.name}  vs  B: ${cmp.b.name}` : ""}</DialogDescription>
          </DialogHeader>
          {cmp && <CompareView data={cmp} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
